import maplibregl from 'maplibre-gl';
import { forwardRef, useEffect, useRef } from 'react';
import { useState } from 'react';
import ReactDOM from 'react-dom';
import * as turf from '@turf/turf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShippingFast } from '@fortawesome/free-solid-svg-icons';
import { LoadingOutlined } from '@ant-design/icons';
import MapDefaults from '../../constants/MapDefaults';
import CanteenTypes from '../../constants/CanteenTypes';
import config from '../../config';
import arrow from '../../images/Arrow.svg';
import userHeadingCone from '../../images/user-heading-cone.svg';

const geojsonInit = { type: 'FeatureCollection', features: [] };

const HKUSTMap = forwardRef(function ({ className, style, loading, locationId, baseMapOnClick, mapOnClick, locationCoordinates, navEdges, geojson, cadGeojson, baseGeojson, navNodes, pointOfInterests, zoom, initialCenter, popupComponent, filteredLocations, keyword, disableAutoZoom, hideEdgeArrows, userLocation, userHeading, activeFloorId }, ref) {
	const map = useRef(null);
	const marker = useRef(null);
	const popup = useRef(null);
	const map_container = useRef(null);
	const timeout = useRef(null);
	const hoverTimeout = useRef(null);
	const [navEdgeGeojson, setNavEdgeGeojson] = useState(null);
	const [allMapBaseGeojson, setAllmapBaseGeojson] = useState(null);
	const [floorGeojson, setFloorGeojson] = useState(null);
	const [floorCadGeojson, setFloorCadGeojson] = useState(null);
	const [zoomLevel, setZoomLevel] = useState(null);
	const [locationCoordinatesOnClick, setLocationCoordinatesOnClick] = useState(null);
	const [hoveredBuildingId, setHoveredBuildingId] = useState(null);

	// Always-current refs so that stale closures in the style.load handler read live values
	const allMapBaseGeojsonRef = useRef(allMapBaseGeojson);
	const geojsonRef = useRef(geojson);
	const floorGeojsonRef = useRef(floorGeojson);
	const floorCadGeojsonRef = useRef(floorCadGeojson);
	allMapBaseGeojsonRef.current = allMapBaseGeojson;
	geojsonRef.current = geojson;
	floorGeojsonRef.current = floorGeojson;
	floorCadGeojsonRef.current = floorCadGeojson;
	// Live refs for the user (blue dot) indicator so the style.load handler reads current values.
	const userLocationRef = useRef(userLocation);
	const userHeadingRef = useRef(userHeading);
	const activeFloorIdRef = useRef(activeFloorId);
	// Same, for the arrow layer's initial visibility: the effect below can only flip a layer that
	// already exists, so a map that mounts with arrows already suppressed (street view restored
	// from the URL) has to be built that way rather than corrected afterwards.
	const hideEdgeArrowsRef = useRef(hideEdgeArrows);
	hideEdgeArrowsRef.current = hideEdgeArrows;
	userLocationRef.current = userLocation;
	userHeadingRef.current = userHeading;
	activeFloorIdRef.current = activeFloorId;
	// Tracks whether the map style definition is ready to accept sources/layers.
	// map.isStyleLoaded() in MapLibre GL 5.x returns false until all tile caches finish
	// downloading, so we use our own flag set inside the style.load handler instead.
	const isStyleReadyRef = useRef(false);
	// Set to true when addMap was called with an explicit initialCenter so that basemap
	// fitBounds does not override the caller-specified starting position.
	//
	// It only guards that opening view: cleared the moment the camera is moved to a place the
	// user asked for (a floor plan, a pin), so that closing that place fits the campus again.
	// Mobile always passes an initialCenter, so leaving it set would make "fit the whole campus"
	// unreachable there for the life of the map — which is exactly the zoom-out expected when the
	// location card is dismissed.
	const hasInitialCenterRef = useRef(false);
	// Set while the camera is deliberately parked on the user's position. Opening their
	// floor loads its plan, its nav nodes and its POIs over several responses, and each
	// arrival fits the map to the whole plan — which would zoom straight back out of the
	// position the user just asked to see. Released as soon as they move the map
	// themselves, leave their floor or ask for a route, so it only wins that race and
	// never takes the map away from them afterwards.
	const centredOnUserRef = useRef(false);
	// Whether the user has moved this map by hand at all. The camera is theirs from then on, so
	// positioning finding them on its own no longer gets to move it (see panToUserLocation).
	const userMovedMapRef = useRef(false);
	// The floor plan currently drawn, its bounds, and whether the camera was moved to show it.
	// Lets an arriving plan be recognised as another floor of the building already on screen
	// rather than a new place to fly to. See fitToFloorPlan.
	const shownFloorPlanRef = useRef(null);
	// The pin the camera has already been flown to. Floor data is re-fetched whenever the floor
	// changes, and the resulting `loading` cycle re-runs the effect that flies to the pin.
	const flownToCoordinatesRef = useRef(null);
	// The overlay drawn while `disableAutoZoom` held (the 360 graph), and whether it is being taken
	// off. Hiding it is not a request to look anywhere, so neither the render that still carries it
	// nor the empty set replacing it may re-fit the plan or fly back to the pin — which, once the
	// user had panned, yanked the map away every time 360 mode was switched off or a dragged 360
	// figure was put back.
	const autoZoomSuppressedEdgesRef = useRef(null);
	const releasingAutoZoomRef = useRef(false);
	const previousDisableAutoZoomRef = useRef(disableAutoZoom);

	// ANCHOR handlers
	const handleMapOnClick = (e) => {
		const features = map.current.queryRenderedFeatures(e.point, {
			layers: ['layer_nav_nodes', 'layer_nav_nodes_label', 'layer_nav_nodes_circles']
		});

		if (!features.length) return;

		const clickLngLat = e.lngLat;
		const clickPoint = [clickLngLat.lng, clickLngLat.lat];

		let closestFeature = null;
		let minDistance = Infinity;

		features.forEach(feature => {
			const { longitude, latitude } = feature.properties || {};
			if (longitude && latitude) {
				const featurePoint = [parseFloat(longitude), parseFloat(latitude)];
				const distance = turf.distance(clickPoint, featurePoint, { units: 'meters' });
				if (distance < minDistance) {
					minDistance = distance;
					closestFeature = feature;
				}
			}
		});

		if (!closestFeature) return;

		const { properties } = closestFeature || {};
		const { longitude, latitude } = properties || {};

		if (longitude, latitude)
			setLocationCoordinatesOnClick([parseFloat(longitude), parseFloat(latitude)]);
		mapOnClick({ ...e, features: [closestFeature] });
	};

	const handleResetOrientation = () => {
		if (map.current && map.current.getBearing() !== 0) {
			map.current.setBearing(0, { duration: 500 });
		}
	};

	const handleHoverOnNodes = (e) => {
		// NOTE Reference
		// 	if (map.current.getLayer('layer_nav_nodes_label')) {
		// 		const features = map.current.queryRenderedFeatures(e.point, { layers: ['layer_nav_nodes_label'] });
		// 		if (features.length > 0 && map.current.getZoom() >= MapDefaults.zoom.min + 1.5) {
		// 			const typeDisplaySetting = features[0].properties.type_display_setting;
		// 			if (typeDisplaySetting === 'show_location_name' || typeDisplaySetting === 'show_location_name_and_icon') {
		// 				map.current.getCanvas().style.cursor = 'pointer';
		// 			}
		// 		}
		// 	}

		if (map.current.getZoom() >= MapDefaults.zoom.min) {
			if (e.type === 'mouseover' || e.type === 'mousemove') {
				const { properties } = e.features[0] || {};
				const { typeDisplaySetting } = properties || {};
				if (typeDisplaySetting === 'show_location_name' || typeDisplaySetting === 'show_location_name_and_icon') {
					map.current.getCanvas().style.cursor = 'pointer';
				}

				map.current.getCanvas().style.cursor = 'pointer';
			}
			if (e.type === 'mouseleave')
				map.current.getCanvas().style.cursor = 'default';
		}
	};

	const handleHoverOnBaseGeojson = (e) => {
		if (geojson || map.current.getZoom() < MapDefaults.zoom.min) return;

		const features = map.current.queryRenderedFeatures(e.point, { layers: ['layer_base_geojson'] });
		const feature = features[0];

		if (e.type === 'mouseover' || e.type === 'mousemove') {
			if (feature && feature.properties && feature.properties.building_id) {
				const buildingId = feature.properties.building_id;
				setHoveredBuildingId(buildingId);
				map.current.getCanvas().style.cursor = 'pointer';
				map.current.setPaintProperty('layer_base_geojson', 'fill-color', [
					'case',
					['==', ['get', 'building_id'], buildingId],
					'#B7B7B7',
					'#CCCCCC'
				]);
				map.current.setPaintProperty('layer_base_geojson_outline', 'line-color', [
					'case',
					['==', ['get', 'building_id'], buildingId],
					'#B7B7B7',
					'#CCCCCC'
				]);
			}
		} else if (e.type === 'mouseleave') {
			setHoveredBuildingId(null);
			map.current.getCanvas().style.cursor = 'default';
			map.current.setPaintProperty('layer_base_geojson', 'fill-color', '#CCCCCC');
			map.current.setPaintProperty('layer_base_geojson_outline', 'line-color', '#CCCCCC');
		}
	};

	const handleHoverOnPOIs = (e) => {
		if (map.current.getZoom() >= MapDefaults.zoom.min) {
			if (e.type === 'mouseover' || e.type === 'mousemove') {
				const { properties } = e.features[0] || {};
				const { type_display_setting } = properties || {};
				if (type_display_setting === 'show_icon' || type_display_setting === 'show_location_name_and_icon' || type_display_setting === 'show_location_name') {
					map.current.getCanvas().style.cursor = 'pointer';
				}
			}
			if (e.type === 'mouseleave')
				map.current.getCanvas().style.cursor = 'default';
		}
	};

	const addMap = (center = MapDefaults.mainCentroid) => {
		isStyleReadyRef.current = false;
		shownFloorPlanRef.current = null;
		flownToCoordinatesRef.current = null;
		hasInitialCenterRef.current = center !== MapDefaults.mainCentroid;
		resetMap();

		map.current = new maplibregl.Map({
			container: map_container.current,
			center,
			zoom: zoom || MapDefaults.zoom.min,
			interactive: true,
			attributionControl: false,
			pitchWithRotate: false,
			minZoom: MapDefaults.zoom.min, // 15
			maxzoom: MapDefaults.zoom.max, // 20
			maxBounds: MapDefaults.extent, // [114.255881140, 22.329426281], [114.275638360, 22.343048531]
			tileSize: MapDefaults.tileSize, // 512
			// style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json'
			style: {
				version: 8,
				sources: {
					HKUSTTiles: {
						type: 'raster',
						tiles: [`${config.asset}/tiles/{z}/{x}/{y}.png`],
						tileSize: MapDefaults.tileSize // 512
						// bounds: MapDefaults.extent
					}
				},
				layers: [{ id: 'hkust-tiles', type: 'raster', source: 'HKUSTTiles' }],
				glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf'
			}
		});

		map.current.getCanvas().style.cursor = 'default';

		map.current.on('style.load', () => {
			isStyleReadyRef.current = true;
			addLayerBaseGeojson();
			addLayerGeojson();
			addLayerCadGeojson();
			addLayerNavEdges();
			addLayerStartPoint();
			addLayerEndPoint();
			addLayerNavNodes();
			addLayerUserLocation();
			updateUserLocation();
			// Apply basemap data and labels immediately if they arrived before the style finished loading
			if (allMapBaseGeojsonRef.current?.features?.length > 0) {
				map.current.getSource('source_base_geojson').setData(allMapBaseGeojsonRef.current);
				updateBaseGeojsonLabels();
				if (!hasInitialCenterRef.current) {
					map.current.fitBounds(turf.bbox(allMapBaseGeojsonRef.current), { padding: 50 });
				}
				fitToFloorPlan(floorGeojsonRef.current);
			}

			// Same for the floor plan itself. It is normally still in flight while the style loads,
			// but a map that mounts onto a floor the store already holds — coming back from the 360
			// view — has it in hand on the first render, before these sources exist. The effects
			// that fill them only re-run when the data changes, and a refetch of the same floor
			// hands back the identical object, so without this the plan never gets drawn at all.
			if (floorGeojsonRef.current) {
				map.current.getSource('source_geojson').setData(floorGeojsonRef.current);
			}
			if (floorCadGeojsonRef.current) {
				map.current.getSource('source_cad_geojson').setData(floorCadGeojsonRef.current);
				if (map.current.getLayer('layer_geojson_outline')) {
					map.current.setLayoutProperty('layer_geojson_outline', 'visibility', 'none');
				}
			}

			marker.current = new maplibregl.Marker({ color: '#FF0000' });
			popup.current = new maplibregl.Popup({ offset: [-20, 0], anchor: 'right', closeButton: false });
		});

		map.current.on('zoomend', (e) => {
			const zoom = map.current.getZoom();
			setZoomLevel(zoom);
		});

		// Moving the map by hand hands control back: from here the auto-fits below are
		// welcome again. `originalEvent` is what distinguishes a gesture from our own
		// flyTo / fitBounds, which raise the same events.
		['dragstart', 'zoomstart', 'rotatestart', 'pitchstart'].forEach((event) => {
			map.current.on(event, (e) => {
				if (e && e.originalEvent) {
					centredOnUserRef.current = false;
					userMovedMapRef.current = true;
				}
			});
		});

		map.current.resize();
		ref.current.map = map.current;
		ref.current.handleResetOrientation = handleResetOrientation;
		ref.current.flyToUserLocation = flyToUserLocation;
		ref.current.panToUserLocation = panToUserLocation;
	};

	// ANCHOR user location (blue dot)
	const addLayerUserLocation = () => {
		if (!map.current || !isStyleReadyRef.current) return;

		// Accuracy halo (geographically-sized polygon, meters).
		if (!map.current.getSource('source_user_accuracy')) {
			map.current.addSource('source_user_accuracy', { type: 'geojson', data: geojsonInit });
			map.current.addLayer({
				id: 'layer_user_accuracy',
				type: 'fill',
				source: 'source_user_accuracy',
				paint: {
					'fill-color': '#1A73E8',
					'fill-opacity': 0.15
				}
			});
		}

		// The blue dot itself (fixed pixel size, Google-maps style).
		if (!map.current.getSource('source_user_location')) {
			map.current.addSource('source_user_location', { type: 'geojson', data: geojsonInit });

			// Which way the phone is pointing, drawn under the dot and rotated with the map so it
			// keeps pointing at the same part of the building as the user turns the view. Shares
			// the dot's source: the heading rides on the same feature, and the layer draws nothing
			// when it is absent (no compass, or one too unsure to be worth trusting).
			map.current.addLayer({
				id: 'layer_user_heading',
				type: 'symbol',
				source: 'source_user_location',
				filter: ['has', 'heading'],
				layout: {
					'icon-image': 'user_heading_cone',
					'icon-size': 0.85,
					'icon-rotate': ['get', 'heading'],
					'icon-rotation-alignment': 'map',
					'icon-pitch-alignment': 'map',
					'icon-allow-overlap': true,
					'icon-ignore-placement': true
				}
			});

			map.current.addLayer({
				id: 'layer_user_location',
				type: 'circle',
				source: 'source_user_location',
				paint: {
					'circle-radius': 8,
					'circle-color': '#1A73E8',
					'circle-stroke-width': 3,
					'circle-stroke-color': '#FFFFFF',
					'circle-pitch-alignment': 'map'
				}
			});
		}
	};

	// Pushes the current userLocation prop into the map sources. Shows the dot only when
	// the displayed floor matches the user's floor (or when no floor filter is active).
	const updateUserLocation = () => {
		if (!map.current || !isStyleReadyRef.current) return;
		if (!map.current.getSource('source_user_location')) return;

		const loc = userLocationRef.current;
		const floorMatches = !loc?.building_floor_id
			|| !activeFloorIdRef.current
			|| String(activeFloorIdRef.current) === String(loc.building_floor_id);

		if (!loc || typeof loc.longitude !== 'number' || typeof loc.latitude !== 'number' || !floorMatches) {
			map.current.getSource('source_user_location').setData(geojsonInit);
			map.current.getSource('source_user_accuracy').setData(geojsonInit);
			return;
		}

		const point = [loc.longitude, loc.latitude];
		const heading = userHeadingRef.current;
		map.current.getSource('source_user_location').setData({
			type: 'FeatureCollection',
			features: [{
				type: 'Feature',
				geometry: { type: 'Point', coordinates: point },
				// Omitted rather than nulled when unknown, so the cone layer's `has` filter drops it.
				properties: typeof heading?.degrees === 'number' ? { heading: heading.degrees } : {}
			}]
		});

		const accuracyMeters = typeof loc.accuracy === 'number' && loc.accuracy > 0 ? loc.accuracy : 5;
		map.current.getSource('source_user_accuracy').setData(
			turf.circle(point, accuracyMeters, { steps: 48, units: 'meters' })
		);
	};

	const flyToUserLocation = () => {
		const loc = userLocationRef.current;
		if (!map.current || !loc || typeof loc.longitude !== 'number') return;
		centredOnUserRef.current = true;
		map.current.flyTo({ center: [loc.longitude, loc.latitude], zoom: 20, duration: 800 });
	};

	// The wide "here you are": brings the dot to the middle of the map at the zoom it already has,
	// the way a maps app opens on your position. `offset` shifts that middle, in pixels, onto the
	// part of the map not covered by overlays. Stands aside - returning false - once the user has
	// moved the map themselves, rather than yanking it away from what they are looking at.
	const panToUserLocation = (offset = [0, 0]) => {
		const loc = userLocationRef.current;
		if (!map.current || !loc || typeof loc.longitude !== 'number' || userMovedMapRef.current) return false;

		// This is now the opening view, so a campus fit arriving late (the base map loading after
		// the fix) must not replace it - the same protection an explicit initialCenter gets.
		hasInitialCenterRef.current = true;
		map.current.easeTo({ center: [loc.longitude, loc.latitude], offset, duration: 800 });
		return true;
	};

	// Whether the floor-plan auto-fits should stand aside for the user's position.
	// Leaving their floor is a decision to look somewhere else, so it releases the hold
	// for good rather than re-arming when they come back.
	const holdingUserCentre = () => {
		if (!centredOnUserRef.current) return false;

		const floorId = userLocationRef.current?.building_floor_id;
		const onUserFloor = !floorId
			|| !activeFloorIdRef.current
			|| String(activeFloorIdRef.current) === String(floorId);
		if (!onUserFloor) centredOnUserRef.current = false;

		return centredOnUserRef.current;
	};

	const bboxesOverlap = (a, b) => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

	// Fits the camera to a floor plan as it arrives, and reports whether it moved the camera.
	//
	// A plan that only replaces another floor of the building already on screen leaves the camera
	// alone. The floors of a building sit on top of each other, so re-fitting shows the same
	// footprint again at the cost of the zoom the user had chosen — and the floor changes on its
	// own while positioning follows them up a staircase, so that zoom would be thrown away every
	// time they change level. A plan that is somewhere else (the first floor opened from the
	// campus overview, another building, or the same one after the user has panned off it) is
	// still flown to, or it would load out of sight.
	const fitToFloorPlan = (plan) => {
		if (!map.current || !plan) return false;

		// A plan on screen means the map has left whatever opening view it was given, so the
		// initial centre no longer needs protecting from the campus fit.
		hasInitialCenterRef.current = false;

		// One arriving plan reaches both callers below, and each answer has to match the first:
		// they act on the verdict, and the second caller must not fit the plan all over again.
		const shown = shownFloorPlanRef.current;
		if (shown && shown.plan === plan) return shown.fitted;

		// Called for its side effect too: it releases the hold once the user leaves their floor.
		const holding = holdingUserCentre();
		const planBbox = turf.bbox(plan);
		const view = map.current.getBounds();
		const replacesPlanInView = shown
			&& bboxesOverlap(planBbox, shown.bbox)
			&& bboxesOverlap(planBbox, [view.getWest(), view.getSouth(), view.getEast(), view.getNorth()]);

		const fitted = !holding && !replacesPlanInView;
		shownFloorPlanRef.current = { plan, bbox: planBbox, fitted };

		if (fitted) map.current.fitBounds(planBbox, { padding: 100 });
		return fitted;
	};

	const resetMap = () => {
		// labels
		resetLabels();
		// nav nodes
		resetNavNodes();
		// nav edges
		resetNavEdges();
		// start point
		resetStartPoint();
		// end point
		resetEndPoint();
		// geojson
		resetGeojson();
		// base geojson
		resetBaseGeojson();
		// canteen
		resetCanteens();
		// AED
		resetAEDs();
		// Lift Shafts
		resetLiftShafts();

		if (map.current) {
			map.current.off('click', baseMapOnClick);
			map.current.remove();
			map.current = null;
		}

		if (marker.current) {
			marker.current.remove();
			marker.current = null;
		}

		if (popup.current) {
			popup.current.remove();
			popup.current = null;
		}
	};

	const resetCanteens = () => {
		if (map.current) {
			if (map.current.getLayer('layer_canteens')) {
				map.current.off('mouseover', 'layer_canteens', handleHoverOnPOIs);
				map.current.off('mousemove', 'layer_canteens', handleHoverOnPOIs);
				map.current.off('mouseleave', 'layer_canteens', handleHoverOnPOIs);
				map.current.removeLayer('layer_canteens');
			}
			if (map.current.getSource('source_canteens')) {
				map.current.removeSource('source_canteens');
			}

			// Remove canteen icons from sprite sheet
			filteredLocations?.forEach((loc) => {
				if (CanteenTypes.includes(loc.type_name) && map.current.hasImage(loc._id)) {
					map.current.removeImage(loc._id);
				}
			});
		}
	};

	const resetAEDs = () => {
		if (map.current) {
			if (map.current.getLayer('layer_aeds')) {
				map.current.off('mouseover', 'layer_aeds', handleHoverOnPOIs);
				map.current.off('mousemove', 'layer_aeds', handleHoverOnPOIs);
				map.current.off('mouseleave', 'layer_aeds', handleHoverOnPOIs);
				map.current.removeLayer('layer_aeds');
			}
			if (map.current.getSource('source_aeds')) {
				map.current.removeSource('source_aeds');
			}

			// Remove AED icons from sprite sheet
			filteredLocations?.forEach((loc) => {
				if (loc.type_name === 'AED' && map.current.hasImage(loc._id)) {
					map.current.removeImage(loc._id);
				}
			});
		}
	};

	const resetLiftShafts = () => {
		if (map.current) {
			if (map.current.getLayer('layer_lift_shafts')) {
				map.current.off('mouseover', 'layer_lift_shafts', handleHoverOnPOIs);
				map.current.off('mousemove', 'layer_lift_shafts', handleHoverOnPOIs);
				map.current.off('mouseleave', 'layer_lift_shafts', handleHoverOnPOIs);
				map.current.removeLayer('layer_lift_shafts');
			}
			if (map.current.getSource('source_lift_shafts')) {
				map.current.removeSource('source_lift_shafts');
			}

			// Remove lift shaft icons from sprite sheet
			filteredLocations?.forEach((loc) => {
				if (loc.type_name === 'Lift Shaft' && map.current.hasImage(loc._id)) {
					map.current.removeImage(loc._id);
				}
			});
		}
	};

	// ANCHOR resets
	const resetMarker = () => {
		if (map.current) {
			if (map.current.getLayer('layer_marker'))
				map.current.removeLayer('layer_marker');
			if (map.current.getSource('source_marker'))
				map.current.removeSource('source_marker');
		}
	};

	const resetLabels = () => {
		if (map.current) {
			if (map.current.getLayer('layer_nav_nodes_label')) {
				map.current.off('click', 'layer_nav_nodes_label', handleMapOnClick);
				map.current.off('contextmenu', 'layer_nav_nodes_label', handleMapOnClick);
				map.current.off('mouseover', 'layer_nav_nodes_label', handleHoverOnNodes);
				map.current.off('mousemove', 'layer_nav_nodes_label', handleHoverOnNodes);
				map.current.off('mouseleave', 'layer_nav_nodes_label', handleHoverOnNodes);
				map.current.off('mouseover', 'layer_nav_nodes_label', handleHoverOnNodes);
				map.current.off('mouseleave', 'layer_nav_nodes_label', handleHoverOnNodes);
				map.current.removeLayer('layer_nav_nodes_label');
			}
			if (map.current.getLayer('layer_base_geojson_label')) {
				map.current.removeLayer('layer_base_geojson_label');
			}
		}
	};

	const resetNavNodes = () => {
		if (map.current) {
			if (map.current.getLayer('layer_nav_nodes_circles')) {
				map.current.off('click', 'layer_nav_nodes_circles', handleMapOnClick);
				map.current.off('contextmenu', 'layer_nav_nodes_circles', handleMapOnClick);
				map.current.off('mouseover', 'layer_nav_nodes_circles', handleHoverOnNodes);
				map.current.off('mousemove', 'layer_nav_nodes_circles', handleHoverOnNodes);
				map.current.off('mouseleave', 'layer_nav_nodes_circles', handleHoverOnNodes);
				map.current.removeLayer('layer_nav_nodes_circles');
			}
			if (map.current.getLayer('layer_nav_nodes')) {
				map.current.off('click', 'layer_nav_nodes', handleMapOnClick);
				map.current.off('contextmenu', 'layer_nav_nodes', handleMapOnClick);
				map.current.off('mouseover', 'layer_nav_nodes', handleHoverOnNodes);
				map.current.off('mousemove', 'layer_nav_nodes', handleHoverOnNodes);
				map.current.off('mouseleave', 'layer_nav_nodes', handleHoverOnNodes);
				map.current.off('mouseover', 'layer_nav_nodes', handleHoverOnNodes);
				map.current.off('mouseleave', 'layer_nav_nodes', handleHoverOnNodes);
				map.current.removeLayer('layer_nav_nodes');
			}
			if (map.current.getSource('source_nav_nodes'))
				map.current.removeSource('source_nav_nodes');
		}
	};

	const resetNavEdges = () => {
		if (map.current) {
			if (map.current.getLayer('layer_nav_edges'))
				map.current.removeLayer('layer_nav_edges');
			if (map.current.getLayer('layer_nav_edges_dashed'))
				map.current.removeLayer('layer_nav_edges_dashed');
			if (map.current.getLayer('layer_nav_edges_arrows'))
				map.current.removeLayer('layer_nav_edges_arrows');
			if (map.current.getLayer('route-layer_nav_edges_arrows'))
				map.current.removeLayer('route-layer_nav_edges_arrows');

			if (map.current.getSource('source_nav_edges'))
				map.current.removeSource('source_nav_edges');
		}
	};

	const resetStartPoint = () => {
		if (map.current) {
			if (map.current.getLayer('layer_start_point_base'))
				map.current.removeLayer('layer_start_point_base');
			if (map.current.getLayer('layer_start_point_mid'))
				map.current.removeLayer('layer_start_point_mid');
			if (map.current.getLayer('layer_start_point_top'))
				map.current.removeLayer('layer_start_point_top');

			if (map.current.getSource('source_start_point'))
				map.current.removeSource('source_start_point');
		}
	};

	const resetEndPoint = () => {
		if (map.current) {
			if (map.current.getLayer('layer_end_point'))
				map.current.removeLayer('layer_end_point');

			if (map.current.getSource('source_end_point'))
				map.current.removeSource('source_end_point');
		}
	};

	const resetGeojson = () => {
		if (map.current) {
			if (map.current.getLayer('layer_geojson'))
				map.current.removeLayer('layer_geojson');
			if (map.current.getLayer('layer_geojson_outline'))
				map.current.removeLayer('layer_geojson_outline');
			if (map.current.getLayer('layer_cad_geojson'))
				map.current.removeLayer('layer_cad_geojson');

			if (map.current.getSource('source_geojson'))
				map.current.removeSource('source_geojson');
			if (map.current.getSource('source_cad_geojson'))
				map.current.removeSource('source_cad_geojson');
		}
	};

	const resetBaseGeojson = () => {
		if (map.current) {
			if (map.current.getLayer('layer_base_geojson')) {
				map.current.off('mouseover', 'layer_base_geojson', handleHoverOnBaseGeojson);
				map.current.off('mousemove', 'layer_base_geojson', handleHoverOnBaseGeojson);
				map.current.off('mouseleave', 'layer_base_geojson', handleHoverOnBaseGeojson);
				map.current.removeLayer('layer_base_geojson');
			}
			if (map.current.getLayer('layer_base_geojson'))
				map.current.removeLayer('layer_base_geojson');
			if (map.current.getLayer('layer_base_geojson_outline'))
				map.current.removeLayer('layer_base_geojson_outline');
			if (map.current.getLayer('layer_base_geojson_label'))
				map.current.removeLayer('layer_base_geojson_label');

			if (map.current.getSource('source_base_geojson'))
				map.current.removeSource('source_base_geojson');
			if (map.current.getSource('source_base_geojson_labels'))
				map.current.removeSource('source_base_geojson_labels');
		}
	};

	const resetAllSpecialLayers = () => {
		resetCanteens();
		resetAEDs();
		resetLiftShafts();

		if (map.current) {
			filteredLocations?.forEach(loc => {
				if ([...CanteenTypes, 'AED', 'Lift Shaft'].includes(loc.type_name)) {
					if (map.current.hasImage(loc._id)) {
						map.current.removeImage(loc._id);
					}
				}
			});
		}
	};

	// ANCHOR adds
	const addLayerBaseGeojson = () => {
		// Ensure baseGeojson features have unique IDs — use ref so the style.load handler always gets current data
		const current = allMapBaseGeojsonRef.current;
		const baseGeojsonWithIds = {
			...current,
			features: current?.features.map((feature, index) => ({
				...feature,
				id: feature.id || index,
				properties: {
					...feature.properties,
					building_id: feature.properties.building_id || null
				}
			})) || []
		};

		map.current.addSource('source_base_geojson', {
			type: 'geojson',
			data: { ...geojsonInit, ...(baseGeojsonWithIds && { features: baseGeojsonWithIds.features }) }
		});

		map.current.addLayer({
			type: 'fill',
			id: 'layer_base_geojson',
			source: 'source_base_geojson',
			paint: {
				'fill-color': '#CCCCCC',
				'fill-opacity': 1
			}
		});

		map.current.addLayer({
			type: 'line',
			id: 'layer_base_geojson_outline',
			source: 'source_base_geojson',
			paint: {
				'line-color': '#CCCCCC',
				// 'line-width': 3,
				'line-width': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 1,
					17, 1.5,
					18, 2,
					19, 2.5,
					20, 3
				]
			}
		});

		// Add hover event listeners
		map.current.on('mouseover', 'layer_base_geojson', handleHoverOnBaseGeojson);
		map.current.on('mousemove', 'layer_base_geojson', handleHoverOnBaseGeojson);
		map.current.on('mouseleave', 'layer_base_geojson', handleHoverOnBaseGeojson);
	};

	const addLayerCanteens = () => {
		if (!map.current || !isStyleReadyRef.current) return;

		if (!map.current.getSource('source_canteens')) {
			map.current.addSource('source_canteens', { type: 'geojson', data: geojsonInit });
			map.current.addLayer({
				id: 'layer_canteens',
				type: 'symbol',
				source: 'source_canteens',
				layout: {
					'icon-image': [
						'case',
						['any',
							['==', ['get', 'type_display_setting'], 'show_icon'],
							['==', ['get', 'type_display_setting'], 'show_location_name_and_icon']
						],
						['coalesce', ['image', ['get', 'location_id']], 'default-icon'],
						''
					],
					'icon-size': 1,
					'icon-anchor': 'center',
					'icon-allow-overlap': true,
					'text-field': ['get', 'name'],
					'text-font': ['Open Sans Bold'],
					'text-size': 12,
					'text-anchor': 'top',
					'text-offset': [0, 1.5],
					'text-max-width': 8
				},
				paint: {
					'text-color': '#003366',
					'text-halo-color': '#FFFFFF',
					'text-halo-width': 1
				},
				minzoom: 16
			});

			map.current.on('mouseover', 'layer_canteens', handleHoverOnPOIs);
			map.current.on('mousemove', 'layer_canteens', handleHoverOnPOIs);
			map.current.on('mouseleave', 'layer_canteens', handleHoverOnPOIs);
		}
	};

	const addLayerAEDs = () => {
		if (!map.current || !isStyleReadyRef.current) return;

		if (!map.current.getSource('source_aeds')) {
			map.current.addSource('source_aeds', { type: 'geojson', data: geojsonInit });
			map.current.addLayer({
				id: 'layer_aeds',
				type: 'symbol',
				source: 'source_aeds',
				layout: {
					'icon-image': [
						'case',
						[
							'any',
							['==', ['get', 'type_display_setting'], 'show_icon'],
							['==', ['get', 'type_display_setting'], 'show_location_name_and_icon']
						],
						['coalesce', ['image', ['get', 'location_id']], 'default-icon'],
						''
					],
					'icon-size': 1,
					'icon-anchor': 'center',
					'icon-allow-overlap': true,
					'text-field': ['get', 'name'],
					'text-font': ['Open Sans Bold'],
					'text-size': 12,
					'text-anchor': 'top',
					'text-offset': [0, 1.5],
					'text-max-width': 8
				},
				paint: {
					'text-color': '#003366',
					'text-halo-color': '#FFFFFF',
					'text-halo-width': 1
				},
				minzoom: 16
			});

			map.current.on('mouseover', 'layer_aeds', handleHoverOnPOIs);
			map.current.on('mousemove', 'layer_aeds', handleHoverOnPOIs);
			map.current.on('mouseleave', 'layer_aeds', handleHoverOnPOIs);
		}
	};

	const addLayerLiftShafts = () => {
		if (!map.current || !isStyleReadyRef.current) return;

		if (!map.current.getSource('source_lift_shafts')) {
			map.current.addSource('source_lift_shafts', { type: 'geojson', data: geojsonInit });
			map.current.addLayer({
				id: 'layer_lift_shafts',
				type: 'symbol',
				source: 'source_lift_shafts',
				layout: {
					'icon-image': [
						'case',
						[
							'any',
							['==', ['get', 'type_display_setting'], 'show_icon'],
							['==', ['get', 'type_display_setting'], 'show_location_name_and_icon']
						],
						['coalesce', ['image', ['get', 'location_id']], 'default-icon'],
						''
					],
					'icon-size': 1,
					'icon-anchor': 'center',
					'icon-allow-overlap': true,
					'text-field': ['get', 'name'],
					'text-font': ['Open Sans Bold'],
					'text-size': 12,
					'text-anchor': 'top',
					'text-offset': [0, 1.5],
					'text-max-width': 8
				},
				paint: {
					'text-color': '#003366',
					'text-halo-color': '#FFFFFF',
					'text-halo-width': 1
				},
				minzoom: 16
			});

			map.current.on('mouseover', 'layer_lift_shafts', handleHoverOnPOIs);
			map.current.on('mousemove', 'layer_lift_shafts', handleHoverOnPOIs);
			map.current.on('mouseleave', 'layer_lift_shafts', handleHoverOnPOIs);
		}
	};

	// CAD line drawings sit above the room fill and replace its outline: where a floor has been
	// synced from the Space Management System, the CAD drawing already carries the wall lines plus
	// doors and fittings that the Revit export has no shapes for. Matches how hkust-space-admin
	// renders the same pair.
	const addLayerCadGeojson = () => {
		map.current.addSource('source_cad_geojson', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			type: 'line',
			id: 'layer_cad_geojson',
			source: 'source_cad_geojson',
			paint: {
				'line-color': '#D3D3D3',
				'line-width': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 0.5,
					17, 0.75,
					18, 1,
					19, 1.25,
					20, 1.5
				],
				'line-opacity': 0.8
			}
		});
	};

	const addLayerGeojson = () => {
		map.current.addSource('source_geojson', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			type: 'fill',
			id: 'layer_geojson',
			source: 'source_geojson',
			paint: {
				'fill-color': [
					'case',
					// Locations set to "Hidden from Map" keep their shape but drop their type colour,
					// taking the same grey as the CAD line drawing so nothing about them stands out
					['==', ['get', 'hidden_from_map'], true],
					'#F9FCFF',
					['has', 'type_color_hex'],
					['concat', '#', ['get', 'type_color_hex']],
					'#CCCCCC'
				],
				'fill-opacity': 1
			}
			// minzoom: MapDefaults.zoom.min + 1
		});

		map.current.addLayer({
			type: 'line',
			id: 'layer_geojson_outline',
			source: 'source_geojson',
			paint: {
				'line-color': '#CCCCCC', //#B4BFCB
				// 'line-width': 2
				'line-width': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 0,
					17, 0.5,
					18, 1,
					19, 1.5,
					20, 2
				]
			}
			// minzoom: MapDefaults.zoom.min + 1
		});
	};

	const addLayerNavNodes = () => {
		map.current.addSource('source_nav_nodes', { type: 'geojson', data: geojsonInit });

		// Circle layer for nodes with type_display_setting: 'none' (panorama nodes)
		map.current.addLayer({
			type: 'circle',
			id: 'layer_nav_nodes_circles',
			source: 'source_nav_nodes',
			paint: {
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 3,
					17, 3.5,
					18, 4,
					19, 4.5,
					20, 5
				],
				'circle-color': '#7F94FF',
				'circle-stroke-width': 1,
				'circle-stroke-color': '#FFFFFF'
			},
			filter: [
				'all',
				['==', ['get', 'type_display_setting'], 'none'],
				['>=', ['zoom'], ['case', ['has', 'minZoom'], ['get', 'minZoom'], 15]]
			]
		});

		map.current.addLayer({
			type: 'symbol',
			id: 'layer_nav_nodes',
			source: 'source_nav_nodes',
			layout: {
				'icon-image': [
					'case',
					['any',
						['==', ['get', 'type_display_setting'], 'show_icon'],
						['==', ['get', 'type_display_setting'], 'show_location_name_and_icon']
					],
					['coalesce', ['image', ['get', '_id']], 'default-icon'],
					''
				],
				'icon-size': 1,
				'icon-anchor': 'center',
				'icon-allow-overlap': true,
				'icon-offset': [
					'case',
					['==', ['get', 'type_display_setting'], 'show_location_name_and_icon'],
					['literal', [0, -20]],
					['literal', [0, 0]]
				]
			},
			filter: [
				'all',
				['!=', ['get', 'type_display_setting'], 'none'],
				['>=', ['zoom'], ['case', ['has', 'minZoom'], ['get', 'minZoom'], 19]]
			]
		});

		map.current.addLayer({
			type: 'symbol',
			id: 'layer_nav_nodes_label',
			source: 'source_nav_nodes',
			layout: {
				'text-field': [
					'case',
					['any',
						['==', ['get', 'type_display_setting'], 'show_location_name'],
						['==', ['get', 'type_display_setting'], 'show_location_name_and_icon']
					],
					['get', 'name'],
					''
				],
				'text-variable-anchor': ['center'],
				'text-font': ['Open Sans Bold'], // Open Sans Regular
				'text-size': 14,
				'text-anchor': 'bottom',
				'text-offset': [0, 0],
				'text-max-width': 10,
				'icon-image': 'type-image',
				'icon-size': 1,
				'icon-anchor': 'top'
				// 'icon-offset': [0, 0] // icon position relative to text
			},
			paint: {
				'text-color': '#003366',
				'text-halo-color': '#FFFFFF',
				'text-halo-width': 1
			},
			filter: [
				'>=',
				['zoom'],
				['case', ['has', 'minZoom'], ['get', 'minZoom'], 18]
			]
		});

		map.current.on('click', 'layer_nav_nodes', handleMapOnClick);
		map.current.on('click', 'layer_nav_nodes_label', handleMapOnClick);
		map.current.on('click', 'layer_nav_nodes_circles', handleMapOnClick);
		map.current.on('contextmenu', 'layer_nav_nodes', handleMapOnClick);
		map.current.on('contextmenu', 'layer_nav_nodes_label', handleMapOnClick);
		map.current.on('contextmenu', 'layer_nav_nodes_circles', handleMapOnClick);

		map.current.on('mouseover', 'layer_nav_nodes', handleHoverOnNodes);
		map.current.on('mousemove', 'layer_nav_nodes', handleHoverOnNodes);
		map.current.on('mouseleave', 'layer_nav_nodes', handleHoverOnNodes);
		map.current.on('mouseover', 'layer_nav_nodes_label', handleHoverOnNodes);
		map.current.on('mousemove', 'layer_nav_nodes_label', handleHoverOnNodes);
		map.current.on('mouseleave', 'layer_nav_nodes_label', handleHoverOnNodes);
		map.current.on('mouseover', 'layer_nav_nodes_circles', handleHoverOnNodes);
		map.current.on('mousemove', 'layer_nav_nodes_circles', handleHoverOnNodes);
		map.current.on('mouseleave', 'layer_nav_nodes_circles', handleHoverOnNodes);
	};

	const addLayerNavEdges = () => {
		map.current.addSource('source_nav_edges', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			id: 'layer_nav_edges',
			type: 'line',
			source: 'source_nav_edges',
			layout: {
				'line-join': 'round',
				'line-cap': 'round'
			},
			paint: {
				'line-color': '#0D5AA7',
				// 'line-width': 10,
				'line-width': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 6,
					17, 6.5,
					18, 7,
					19, 7.5,
					20, 8
				],
				'line-opacity': 1
			}
		});

		map.current.addLayer({
			id: 'layer_nav_edges_dashed',
			type: 'line',
			source: 'source_nav_edges',
			layout: {
				'line-join': 'round',
				'line-cap': 'round'
			},
			paint: {
				'line-color': '#0088FF',
				// 'line-width': 7.5,
				'line-width': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 3.5,
					17, 4,
					18, 4.5,
					19, 5,
					20, 5.5
				],
				'line-opacity': 1
			}
		});

		map.current.addLayer({
			id: 'route-layer_nav_edges_arrows',
			type: 'symbol',
			source: 'source_nav_edges',
			layout: {
				'symbol-placement': 'line',
				'symbol-spacing': 220,
				'icon-image': 'arrow_icon',
				'icon-rotate': 90,
				'icon-allow-overlap': true,
				'icon-size': 0.5,
				visibility: hideEdgeArrowsRef.current ? 'none' : 'visible'
			},
			paint: {
				'icon-opacity': 1
			},
			minzoom: 10 // Ensure arrows are visible at lower zoom levels
		});
	};

	const addLayerStartPoint = () => {
		map.current.addSource('source_start_point', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			id: 'layer_start_point_base',
			type: 'circle',
			source: 'source_start_point',
			paint: {
				// 'circle-radius': 18,
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 16,
					17, 16.5,
					18, 17,
					19, 17.5,
					20, 18
				],
				'circle-color': '#819AB0'
			}
		});

		map.current.addLayer({
			id: 'layer_start_point_mid',
			type: 'circle',
			source: 'source_start_point',
			paint: {
				// 'circle-radius': 14,
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 12,
					17, 12.5,
					18, 13,
					19, 13.5,
					20, 14
				],
				'circle-color': '#FFFFFF'
			}
		});

		map.current.addLayer({
			id: 'layer_start_point_top',
			type: 'circle',
			source: 'source_start_point',
			paint: {
				// 'circle-radius': 10,
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 8,
					17, 8.5,
					18, 9,
					19, 9.5,
					20, 10
				],
				'circle-color': '#0088FF'
			}
		});
	};

	const addLayerEndPoint = () => {
		map.current.addSource('source_end_point', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			id: 'layer_end_point',
			type: 'circle',
			source: 'source_end_point',
			paint: {
				// 'circle-radius': 18,
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					16, 16,
					17, 16.5,
					18, 17,
					19, 17.5,
					20, 18
				],
				'circle-color': '#0088FF'
			}
		});
	};

	const addLayerMarker = async () => {
		map.current.addSource('source_marker', { type: 'geojson', data: geojsonInit });
		map.current.addLayer({
			id: 'layer_marker',
			type: 'symbol',
			source: 'source_marker',
			layout: {
				'icon-image': 'custom_icon', //
				'icon-size': 1,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			}
		});
	};

	const updateBaseGeojsonLabels = () => {
		// Use refs so this is safe to call from the style.load handler (stale closure)
		if (!map.current || !isStyleReadyRef.current || !allMapBaseGeojsonRef.current || geojsonRef.current) return;

		// Create label GeoJSON
		const labelGeojson = { type: 'FeatureCollection', features: [] };
		const featuresByName = {};
		allMapBaseGeojsonRef.current.features.forEach(feature => {
			const name = feature.properties.name || 'unnamed';
			if (!featuresByName[name]) {
				featuresByName[name] = [];
			}
			featuresByName[name].push(feature);
		});

		for (const name in featuresByName) {
			if (name !== 'unnamed') {
				const features = featuresByName[name];
				const combined = turf.combine({ type: 'FeatureCollection', features });
				const centroid = turf.centroid(combined);
				centroid.properties = { name };
				labelGeojson.features.push(centroid);
			}
		}

		// Add or update label source
		if (!map.current.getSource('source_base_geojson_labels')) {
			map.current.addSource('source_base_geojson_labels', {
				type: 'geojson',
				data: labelGeojson
			});
		} else {
			map.current.getSource('source_base_geojson_labels').setData(labelGeojson);
		}

		// Add label layer if not already present
		if (!map.current.getLayer('layer_base_geojson_label')) {
			map.current.addLayer({
				type: 'symbol',
				id: 'layer_base_geojson_label',
				source: 'source_base_geojson_labels',
				layout: {
					'text-field': ['get', 'name'],
					'text-variable-anchor': ['center'],
					'text-font': ['Open Sans Bold'],
					'text-size': [
						'interpolate',
						['linear'],
						['zoom'],
						16, 12,
						18, 14,
						20, 16
					],
					'text-anchor': 'center',
					'text-offset': [0, 0],
					'text-max-width': 8,
					'text-allow-overlap': false,
					'text-ignore-placement': false
				},
				paint: {
					'text-color': '#003366',
					'text-halo-color': '#FFFFFF',
					'text-halo-width': 1
				},
				minzoom: 12
			});
		}
	};

	// ANCHOR useEffects
	useEffect(() => {
		addMap(initialCenter);
		return () => {
			clearTimeout(hoverTimeout.current);
			resetMap();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (baseGeojson) {
			const parser_baseGeojson = JSON.parse(baseGeojson);
			const baseGeojsonWithIds = {
				...parser_baseGeojson,
				features: parser_baseGeojson.features.map((feature, index) => ({
					...feature,
					id: feature.id || index
				}))
			};
			setAllmapBaseGeojson(baseGeojsonWithIds);

			// FIXME basemap on click
			if (map.current && baseMapOnClick) {
				map.current.on('click', baseMapOnClick);
				map.current.on('contextmenu', baseMapOnClick);

			}

		}

	}, [baseGeojson]);

	useEffect(() => {
		if (!map.current || !isStyleReadyRef.current || !filteredLocations || geojson) {
			if (geojson || !keyword) {
				resetAllSpecialLayers();
			}
			return;
		}

		resetAllSpecialLayers();

		// Process based on keyword
		if (keyword === 'Canteen') {
			addLayerCanteens();
			const canteenData = processLocations(CanteenTypes);
			setTimeout(() => {
				if (map.current.getSource('source_canteens')) {
					map.current.getSource('source_canteens').setData(canteenData);
				}
			}, 100);
		}
		else if (keyword === 'AED') {
			addLayerAEDs();
			const aedData = processLocations('AED');
			setTimeout(() => {
				if (map.current.getSource('source_aeds')) {
					map.current.getSource('source_aeds').setData(aedData);
				}
			}, 100);
		} else if (keyword === 'Lift Shaft') {
			addLayerLiftShafts();
			const liftShaftData = processLocations('Lift Shaft');
			setTimeout(() => {
				if (map.current.getSource('source_lift_shafts')) {
					map.current.getSource('source_lift_shafts').setData(liftShaftData);
				}
			}, 100);
		}

		// Load icons for both types
		filteredLocations
			.filter(loc => CanteenTypes.includes(loc.type_name) || loc.type_name === 'AED' || loc.type_name === 'Lift Shaft')
			.forEach((location) => {
				if (location?.icon?.file_content) {
					const img = new Image(24, 24);
					img.src = location.icon.file_content;
					img.onload = () => {
						if (!map.current.hasImage(location._id)) {
							map.current.addImage(location._id, img);
						}
					};
				}
			});

		function processLocations(type) {
			// `type` is one type name or a group of them — the Canteen shortcut spans four.
			const type_names = [].concat(type);
			const features = filteredLocations
				.filter(loc => {

					const isValid = type_names.includes(loc.type_name) &&
						!isNaN(parseFloat(loc.longitude)) &&
						!isNaN(parseFloat(loc.latitude));

					if (!isValid) {
						console.warn(`Invalid ${type_names.join('/')} location coordinates:`, loc);
					}
					return isValid;
				})
				.map(loc => {
					return {
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [parseFloat(loc.longitude), parseFloat(loc.latitude)]
						},
						properties: {
							location_id: loc._id,
							name: loc.name,
							type_display_setting: loc.type_display_setting,
							type_name: loc.type_name,
							building_name: loc.building_name,
							building_floor_name: loc.building_floor_name,
							building_id: loc.building_id,
							building_floor_id: loc.building_floor_id,
							latitude: loc.latitude,
							longitude: loc.longitude,
							remote_id: loc.remote_id
						}
					};
				});

			return {
				type: 'FeatureCollection',
				features: features
			};
		}

		if (baseGeojson && !locationCoordinates) {
			const centroid = turf.centroid(JSON.parse(baseGeojson));
			map.current.flyTo({
				center: centroid.geometry.coordinates,
				zoom: 15,
				duration: 800
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredLocations, geojson, baseGeojson, locationCoordinates, keyword]);

	useEffect(() => {
		if (!map.current || !isStyleReadyRef.current) return;

		if (geojson) {
			if (map.current.getLayer('layer_base_geojson_label')) {
				map.current.setLayoutProperty('layer_base_geojson_label', 'visibility', 'none');
			}
		} else {
			updateBaseGeojsonLabels();
			if (map.current.getLayer('layer_base_geojson_label')) {
				map.current.setLayoutProperty('layer_base_geojson_label', 'visibility', 'visible');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [geojson, allMapBaseGeojson]);

	useEffect(() => {
		setFloorGeojson(geojson || null);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [geojson]);

	useEffect(() => {
		setFloorCadGeojson(cadGeojson || null);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cadGeojson]);

	useEffect(() => {
		if (!zoomLevel) return;

		if (marker.current) {
			marker.current.getElement().style.display = zoomLevel <= 17 || !locationCoordinates ? 'none' : 'block';
		}
	}, [zoomLevel, locationCoordinates]);

	useEffect(() => {
		if (map.current.getSource('source_geojson')) {
			if (floorGeojson) {
				map.current.getSource('source_geojson').setData(floorGeojson);
			}
			else {
				map.current.getSource('source_geojson').setData(geojsonInit);
				// Back on the campus overview: the next plan is a place being opened, not a floor
				// being changed, so it is flown to again.
				shownFloorPlanRef.current = null;
				if (marker.current) {
					marker.current.remove();
					marker.current = new maplibregl.Marker({ color: '#FF0000' });
				}
			}
		}

	}, [floorGeojson]);

	useEffect(() => {
		if (!map.current || !map.current.getSource('source_cad_geojson')) return;

		map.current.getSource('source_cad_geojson').setData(floorCadGeojson || geojsonInit);

		// Without a CAD drawing the rooms still need their own outline, so it is only hidden while
		// one is present.
		if (map.current.getLayer('layer_geojson_outline')) {
			map.current.setLayoutProperty(
				'layer_geojson_outline',
				'visibility',
				floorCadGeojson ? 'none' : 'visible'
			);
		}
	}, [floorCadGeojson]);

	useEffect(() => {
		if (!map.current || !isStyleReadyRef.current) return;
		if ((allMapBaseGeojson && allMapBaseGeojson.features && allMapBaseGeojson.features.length > 0) && map.current.getSource('source_base_geojson')) {
			map.current.getSource('source_base_geojson').setData(allMapBaseGeojson);
			updateBaseGeojsonLabels();
			if (floorGeojson) {
				fitToFloorPlan(floorGeojson);
			} else if (!hasInitialCenterRef.current) {
				map.current.fitBounds(turf.bbox(allMapBaseGeojson), { padding: 50 });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allMapBaseGeojson, floorGeojson]);

	// Heading is in here too: it arrives far more often than the position (the user turns on the
	// spot without moving), and it rides on the dot's own feature, so the cone only follows the
	// phone if a new heading pushes the source again.
	useEffect(() => {
		updateUserLocation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userLocation, userHeading, activeFloorId]);

	useEffect(() => {
		if (popup.current)
			popup.current.remove();
		if (!loading) {
			if (marker.current) {
				if (locationCoordinates) {
					marker.current.getElement().style.cursor = 'default';
					marker.current.getElement().style.display = 'block';
					marker.current.setLngLat(locationCoordinates).addTo(map.current);
					// Fly only when this is a pin the camera has not been sent to yet. Changing floor
					// re-fetches the floor data, and this effect runs again on that `loading` cycle
					// with the pin unchanged — flying to it a second time would drag the map back off
					// wherever the user had zoomed to, at the moment they changed level.
					if (flownToCoordinatesRef.current !== locationCoordinates) {
						flownToCoordinatesRef.current = locationCoordinates;
						// Same as in fitToFloorPlan: the opening view has been left behind, so the
						// campus fit is welcome again once this pin is cleared.
						hasInitialCenterRef.current = false;
						map.current.flyTo({
							center: locationCoordinates,
							duration: 800,
							zoom: 21
						});
					}
				} else {
					flownToCoordinatesRef.current = null;
					marker.current.remove();
					marker.current = new maplibregl.Marker({ color: '#FF0000' });
				}
			}
		}
	}, [loading, locationCoordinates]);

	// FIXME popup component
	useEffect(() => {
		if (!popup.current || !locationCoordinatesOnClick) return;

		popup.current.setLngLat(locationCoordinatesOnClick);
	}, [locationCoordinatesOnClick]);

	useEffect(() => {
		if (!popup.current || !popupComponent) return;

		const div = document.createElement('div');
		div.className = 'popup-container';
		ReactDOM.render(popupComponent, div);

		// ANCHOR
		popup.current.setDOMContent(div);
		// if (locationCoordinates)
		// 	popup.current.setLngLat(locationCoordinatesOnClick);

		popup.current.addTo(map.current);
		popup.current.on('close', () => ReactDOM.unmountComponentAtNode(div));
	}, [popupComponent]);

	useEffect(() => {
		if (!map.current || !map.current.getSource('source_nav_nodes')) return;

		if (!navNodes) {
			map.current.getSource('source_nav_nodes').setData(geojsonInit);
			return;
		}

		const geojson = {
			type: 'FeatureCollection',
			features: navNodes.map(navNode => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [navNode.longitude, navNode.latitude]
				},
				properties: {
					_id: navNode._id,
					name: navNode.name,
					type_name: navNode.type_name,
					type_display_setting: navNode.type_display_setting,
					longitude: navNode.longitude,
					latitude: navNode.latitude,
					location_id: navNode.location_id,
					remote_id: navNode.remote_id,
					point_of_interest_id: navNode.point_of_interest_id,
					building_floor_id: navNode.building_floor_id,
					point_of_interest_type_id: navNode.point_of_interest_type_id,
					minZoom: navNode?.show_at_zoom_level
				}
			}))
		};

		map.current.getSource('source_nav_nodes').setData(geojson);

		// MapLibre lays symbols out once per source update. An `icon-image` that resolves to an
		// image the map does not have yet drops the symbol entirely (the `default-icon` fallback in
		// the layer's coalesce is not part of any sprite), and registering the image afterwards does
		// not re-layout the tiles that were already parsed. So load the icons, then push the same
		// data again to force the re-layout. Floor views used to get that second update for free
		// when `floorGeojson` arrived; the home screen never selects a floor, which is why
		// "Show at Home Screen" point of interests rendered without their icons.
		let cancelled = false;
		const pendingIcons = navNodes
			.filter(node => node._id && node.icon?.file_content && !map.current.hasImage(node._id))
			.map(node => new Promise((resolve) => {
				const img = new Image(24, 24);
				img.onload = () => {
					if (!cancelled && map.current && !map.current.hasImage(node._id))
						map.current.addImage(node._id, img); // Use _id as the unique image ID
					resolve();
				};
				img.onerror = () => resolve();
				img.src = node.icon.file_content; // Base64 data URL
			}));

		if (!pendingIcons.length) return;

		Promise.all(pendingIcons).then(() => {
			if (cancelled || !map.current) return;
			const source = map.current.getSource('source_nav_nodes');
			if (source) source.setData(geojson);
		});

		return () => {
			cancelled = true;
		};
	}, [floorGeojson, navNodes, keyword]);

	useEffect(() => {
		if (map.current.getSource('source_point_of_interests')) {
			if (floorGeojson && pointOfInterests) {
				map.current.getSource('source_point_of_interests').setData({
					type: 'FeatureCollection',
					features: pointOfInterests.map(pointOfInterest => ({
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [pointOfInterest.longitude, pointOfInterest.latitude]
						},
						properties: {
							_id: pointOfInterest._id,
							name: pointOfInterest.name,
							type_display_setting: pointOfInterest.type_display_setting,
							longitude: pointOfInterest.longitude,
							latitude: pointOfInterest.latitude,
							point_of_interest_type_id: pointOfInterest.point_of_interest_type_id,
							building_floor_id: pointOfInterest.building_floor_id,
							remote_id: pointOfInterest.remote_id
						}
					}))
				});

				pointOfInterests.forEach((pointOfInterest) => {
					const img = new Image();
					if (pointOfInterest.icon) {
						img.src = pointOfInterest.icon.file_content;
						img.height = 24;
						img.width = 24;
						img.onload = () => {
							if (!map.current.hasImage(pointOfInterest._id)) {
								map.current.addImage(pointOfInterest._id, img);
							}
						};
					}
				});
			} else {
				map.current.getSource('source_point_of_interests').setData(geojsonInit);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [floorGeojson, pointOfInterests]);

	useEffect(() => {
		if (!floorGeojson) return;

		if (!loading) {
			if (map.current.getSource('source_nav_edges') && map.current.getSource('source_start_point') && map.current.getSource('source_end_point')) {
				if (navEdges) {
					// Check if navEdges is already a GeoJSON FeatureCollection (for panorama edges)
					// or if it's a coordinate array (for navigation routes)
					const isFeatureCollection = navEdges && typeof navEdges === 'object' && navEdges.type === 'FeatureCollection';

					if (isFeatureCollection) {
						// Use the FeatureCollection directly (panorama edges with multiple line segments)
						setNavEdgeGeojson(navEdges);
						// Don't set start/end points for panorama edges
						map.current.getSource('source_start_point').setData(geojsonInit);
						map.current.getSource('source_end_point').setData(geojsonInit);
					} else {
						// Wrap coordinate array in a single LineString feature (navigation routes)
						setNavEdgeGeojson(() => ({
							type: 'FeatureCollection',
							features: [{
								type: 'Feature',
								geometry: {
									type: 'LineString',
									coordinates: navEdges
								},
								properties: {}
							}]
						}));

						map.current.getSource('source_start_point').setData({
							type: 'FeatureCollection',
							features: [
								{
									type: 'Feature',
									geometry: {
										type: 'Point',
										coordinates: navEdges[0]
									},
									properties: {}
								}
							]
						});

						map.current.getSource('source_end_point').setData({
							type: 'FeatureCollection',
							features: [
								{
									type: 'Feature',
									geometry: {
										type: 'Point',
										coordinates: navEdges[navEdges.length - 1]
									},
									properties: {}
								}
							]
						});
					}
				} else {
					map.current.getSource('source_start_point').setData(geojsonInit);
					map.current.getSource('source_end_point').setData(geojsonInit);
					setNavEdgeGeojson(geojsonInit);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [floorGeojson, navEdges, loading]);

	useEffect(() => {
		if (!map.current || !map.current.getLayer('route-layer_nav_edges_arrows')) return;

		// Hide or show arrows based on hideEdgeArrows prop
		if (hideEdgeArrows) {
			map.current.setLayoutProperty('route-layer_nav_edges_arrows', 'visibility', 'none');
		} else {
			map.current.setLayoutProperty('route-layer_nav_edges_arrows', 'visibility', 'visible');
		}
	}, [hideEdgeArrows]);

	useEffect(() => {
		clearTimeout(timeout.current);
		if (!map.current.getSource('source_nav_edges')) return;

		if (previousDisableAutoZoomRef.current && !disableAutoZoom) releasingAutoZoomRef.current = true;
		previousDisableAutoZoomRef.current = disableAutoZoom;

		if (navEdgeGeojson) {
			map.current.getSource('source_nav_edges').setData(navEdgeGeojson);

			let keepCamera = false;
			if (disableAutoZoom) {
				autoZoomSuppressedEdgesRef.current = navEdgeGeojson;
				releasingAutoZoomRef.current = false;
			} else if (releasingAutoZoomRef.current) {
				// Either the overlay's last frame (the edges replacing it land a render later) or the
				// empty set replacing it: both leave the camera alone. A route arriving in its place is
				// a request of its own and is still shown in full below.
				const isOverlay = navEdgeGeojson === autoZoomSuppressedEdgesRef.current;
				const isEmpty = !navEdgeGeojson.features.length;
				keepCamera = isOverlay || isEmpty;
				if (isEmpty || !isOverlay) {
					releasingAutoZoomRef.current = false;
					autoZoomSuppressedEdgesRef.current = null;
				}
			}

			if (!disableAutoZoom && !keepCamera) {
				if (navEdgeGeojson.features.length) {
					// A route is an explicit request of its own; show all of it.
					centredOnUserRef.current = false;
					map.current.fitBounds(turf.bbox(navEdgeGeojson), { padding: 100 });
				} else if (floorGeojson && fitToFloorPlan(floorGeojson)) {
					// Only worth chasing the pin when the fit above moved the camera off it; a
					// floor swapped under an untouched camera has not lost sight of anything.
					if (!loading && locationCoordinates) {
						timeout.current = setTimeout(() => {
							map.current.flyTo({
								center: locationCoordinates,
								duration: 800,
								zoom: 21
							});
						}, 300);
					}
				}
			}
		}

		// FIXME this is a test
		if (arrow) {
			const img = new Image();
			img.src = arrow;
			img.height = 48;
			img.width = 48;
			img.onload = () => {
				if (!map.current.hasImage('arrow_icon')) {
					map.current.addImage('arrow_icon', img);
				}
			};
		}

		if (userHeadingCone && !map.current.hasImage('user_heading_cone')) {
			const img = new Image();
			img.src = userHeadingCone;
			img.height = 96;
			img.width = 96;
			img.onload = () => {
				if (map.current && !map.current.hasImage('user_heading_cone')) {
					map.current.addImage('user_heading_cone', img);
				}
			};
		}
		// map.current.getSource('source_nav_edges').setData(geojsonInit);
	}, [navEdgeGeojson, floorGeojson, loading, disableAutoZoom]);

	return <>
		{/* { width: '100%', height: '100%', position: 'relative', overflow: 'hidden' } */}
		<div
			className={className}
			style={style}
		>
			{loading &&
				<div className="loading-overlay">
					<LoadingOutlined />
				</div>
			}
			<div
				ref={map_container}
				style={{ height: '100dvh', width: '100%' }}
			>
			</div>
		</div >
	</>;
});

export default HKUSTMap;
